import { APP_ACQUISITION } from '../configs/appAcquisition'
import { APPLE_SEARCH_ADS } from '../configs/appleSearchAds'
import { BENCHMARK } from '../configs/benchmark'
import { BENCHMARK_CREATIVES } from '../configs/benchmarkCreatives'
import { CUSTOMER_INTELLIGENCE } from '../configs/customerIntelligence'
import { PRODUCT_INSIGHTS } from '../configs/productInsights'

import {
  registerReportLayout,
  type ReportLayoutConfig,
  type ReportLayoutKey,
} from '@/features/reports'

// Contract 27 §8 `registerCommerceLayouts` — registry inversion (ledger §A11): this feature
// pushes its layouts INTO 08; 08 imports nothing from here. Exactly the FIVE keys 08 does not
// seed. The other seven module-27 layouts (BUDGET_ALLOCATION, APP_ACQUISITION, STORE_ANALYSIS,
// PRODUCT_PERFORMANCE, CREATIVE_DASHBOARD, HOTEL_ADS, and module 24's RFM_CLUSTERING) live as
// 08 seeds — re-registering a seeded key THROWS by design (`layoutRegistry.ts`). The
// PRODUCT_PERFORMANCE seed handoff was ATTEMPTED under ORP-18953 and STOPPED per its stop-rule:
// registration rides route `beforeEnter`, so a cold deep link to 08's own
// `/ecommerce/product-performance` route would never load this chunk (blank layout); 08's
// `pages.ts` types `layoutKey` closed over its seeds; and the seed's copy keys live in the
// `reports.*` bundle. All three need the page-context registry 08's pages.ts §18 note names.
// The map stays un-exported for the same reason 08 hides
// `SEED_REPORT_LAYOUTS`: a static map others can read is what the inversion removes.
const COMMERCE_REPORT_LAYOUTS = {
  APP_ACQUISITION,
  APPLE_SEARCH_ADS,
  BENCHMARK,
  BENCHMARK_CREATIVES,
  CUSTOMER_INTELLIGENCE,
  PRODUCT_INSIGHTS,
} as const satisfies Record<string, ReportLayoutConfig>

export type CommerceLayoutKey = keyof typeof COMMERCE_REPORT_LAYOUTS

export const COMMERCE_LAYOUT_KEYS = Object.keys(COMMERCE_REPORT_LAYOUTS) as CommerceLayoutKey[]

/**
 * Register this feature's layouts. Idempotent by identity (the registry no-ops a repeat
 * registration of the same config object), so the route-level `beforeEnter` may call it on
 * every navigation without a guard.
 *
 * @returns the combined unregister — test isolation only, never called in production.
 */
export function registerCommerceLayouts(
  register: (key: ReportLayoutKey, config: ReportLayoutConfig) => () => void = registerReportLayout,
): () => void {
  const undos = Object.entries(COMMERCE_REPORT_LAYOUTS).map(([key, config]) =>
    register(key, config),
  )
  return () => {
    for (const undo of undos) undo()
  }
}
